import axios from 'axios';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import React from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { LoadingContainer } from '../../components/LoadingContainer';
import { SITE_LINKS } from '../../constants';
import { useGetEmergencyContact } from '../../hooks/use-get-emergency-contact';
import { EmergencyContact } from '../../models/EmergencyContact';
import { Background } from '../../stories/Background';
import { Button } from '../../stories/Button';
import { Header } from '../../stories/Header';
import { InputFeedback } from '../../stories/InputFeedback';
import { OnboardingNavbar } from '../../stories/OnboardingNavbar';
import { TextInput } from '../../stories/TextInput';

export const OnboardingEmergencyContactPage: React.FC = () => {
  const { emergencyContact, isFetching } = useGetEmergencyContact();
  const __ = useTranslation().t;

  const initialValues: EmergencyContact = {
    contact: emergencyContact?.contact || '',
    contact_relationship: emergencyContact?.contactRelationship || '',
    contact_phone: emergencyContact?.contactPhone || '',
    country: emergencyContact?.country || '',
    state: emergencyContact?.state || '',
    city: emergencyContact?.city || '',
    zip_code: emergencyContact?.zipCode || '',
    address: emergencyContact?.address || '',
  };

  const schema = yup.object().shape({
    contact: yup.string().required(__('fieldRequired')),
    contact_relationship: yup.string().required(__('fieldRequired')),
    contact_phone: yup.string().required(__('fieldRequired')),
    country: yup.string().required(__('fieldRequired')),
    state: yup.string().required(__('fieldRequired')),
    city: yup.string().required(__('fieldRequired')),
    zip_code: yup
      .string()
      .required(__('fieldRequired'))
      .max(16, __('fieldMaxLengthExceeded', { 1: 16 })),
    address: yup.string().required(__('fieldRequired')),
  });

  const onSubmit = (
    values: EmergencyContact,
    { setSubmitting, setFieldError }: FormikHelpers<EmergencyContact>,
  ) => {
    setSubmitting(false);
    axios
      .post('/v4/emergency-contact', values)
      .then((response) => {
        window.location.href = SITE_LINKS.PlacementTestPage;
      })
      .catch((error) => {
        if (error.response.status === 422) {
          const errorFields = Object.keys(error.response.data.errors || {});
          for (let i = 0; i < errorFields.length; i++) {
            setFieldError(errorFields[i], error.response.data.errors[errorFields[i]][0]);
          }
          return;
        }
        return alert(__('errorOccurred'));
      });
  };

  return (
    <>
      <Header type="logo-only" />
      <Background>
        <Container>
          <div className="oejr-page-onboarding oejr-page-onboarding-3">
            <OnboardingNavbar
              backButtonHref={SITE_LINKS.OnboardingPage(`recommendations`)}
              numberOfCompletedSteps={1}
              totalSteps={3}
              title={__('provideFollowingInformation')}
            />
            <h1 className="oejr-onboarding-main-argument">{__('letsKeepInTouch')}</h1>
            {isFetching ? (
              <LoadingContainer />
            ) : (
              <Formik initialValues={initialValues} validationSchema={schema} onSubmit={onSubmit}>
                {({ isSubmitting }) => (
                  <Form>
                    <div className="oejr-onboarding-form d-flex flex-column flex-md-row">
                      <div className="oejr-onboarding-form-column">
                        <div className="oejr-onboarding-form-section">
                          <h4>{__('emergencyContact')}</h4>
                          <Field
                            type="custom_text-letters-only"
                            name="contact"
                            label={__('contactName') + ' *'}
                            component={TextInput}
                            inputGroupClassName="oejr-input-group-first-child"
                          />
                          <ErrorMessage
                            name="contact"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                        </div>
                        <div className="oejr-onboarding-form-section oejr-onboarding-form-section-2">
                          <h4>{__('contactInformation')}</h4>
                          <Field
                            type="custom_text-letters-only"
                            name="contact_relationship"
                            label={__('relationshipWithStudent') + ' *'}
                            component={TextInput}
                            inputGroupClassName="oejr-input-group-first-child"
                          />
                          <ErrorMessage
                            name="contact_relationship"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                          <Field
                            type="tel"
                            name="contact_phone"
                            label={__('phoneNumber') + ' *'}
                            component={TextInput}
                          />
                          <ErrorMessage
                            name="contact_phone"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="oejr-onboarding-form-column">
                        <div className="oejr-onboarding-form-section">
                          <h4>{__('whereAreYouLocated')}</h4>
                          <Field
                            type="custom_text-letters-only"
                            name="country"
                            label={__('country') + ' *'}
                            component={TextInput}
                            inputGroupClassName="oejr-input-group-first-child"
                          />
                          <ErrorMessage
                            name="country"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                          <Field
                            type="custom_text-letters-only"
                            name="state"
                            label={__('state') + ' *'}
                            component={TextInput}
                          />
                          <ErrorMessage
                            name="state"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                          <Field
                            type="custom_text-letters-only"
                            name="city"
                            label={__('city') + ' *'}
                            component={TextInput}
                          />
                          <ErrorMessage
                            name="city"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                          <Field
                            type="tel"
                            name="zip_code"
                            label={__('zipCode') + ' *'}
                            component={TextInput}
                            maxLength={16}
                          />
                          <ErrorMessage
                            name="zip_code"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                          <Field
                            type="text"
                            name="address"
                            label={__('address') + ' *'}
                            component={TextInput}
                          />
                          <ErrorMessage
                            name="address"
                            component={({ children }) => (
                              <InputFeedback
                                state="error"
                                text={children}
                                icon="exclamation-circle"
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <Button
                        icon="arrow-right"
                        label={__('save')}
                        variant="primary"
                        size="lg"
                        type="submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </Container>
      </Background>
    </>
  );
};
